import { useEffect, useLayoutEffect, useRef } from 'react';
import { matchesHotkey, type HotkeyId } from '@/lib/hotkeys';

/**
 * What a shortcut does. It takes no event: the chord is already matched and
 * the default already prevented, so there is nothing left to inspect.
 */
export type HotkeyHandler = () => void;

const subscribers = new Map<HotkeyId, Set<HotkeyHandler>>();
let listening = false;

/**
 * One document listener for every app-global shortcut, so the guards below are
 * applied identically to all of them instead of being re-derived (or
 * forgotten) at each call site.
 */
function onDocumentKeyDown(event: KeyboardEvent): void {
  // Something closer to the user already handled the key — a field that claims
  // the comma, a dialog, a menu. It wins.
  if (event.defaultPrevented) return;
  // A held chord must not fire once per repeat: every shortcut here opens a
  // surface or navigates, and repeats would stack history entries and re-clear
  // the query under the user's hands.
  if (event.repeat) return;
  for (const [id, handlers] of subscribers) {
    if (handlers.size === 0 || !matchesHotkey(id, event)) continue;
    event.preventDefault();
    // Copy: a handler may unsubscribe (unmount its owner) as it runs.
    for (const handler of [...handlers]) handler();
    return;
  }
}

function subscribe(id: HotkeyId, handler: HotkeyHandler): () => void {
  const handlers = subscribers.get(id) ?? new Set<HotkeyHandler>();
  handlers.add(handler);
  subscribers.set(id, handlers);
  if (!listening) {
    document.addEventListener('keydown', onDocumentKeyDown);
    listening = true;
  }
  return () => {
    handlers.delete(handler);
    if (handlers.size === 0) subscribers.delete(id);
    if (subscribers.size === 0 && listening) {
      document.removeEventListener('keydown', onDocumentKeyDown);
      listening = false;
    }
  };
}

/**
 * Binds an app-global shortcut from the surface that owns it.
 *
 * The chord itself is not passed in: it lives in the `HOTKEYS` table in
 * `@/lib/hotkeys`, which is what keeps the hint the user reads, the key the
 * app listens for, and the conflict check in one place. Render the hint with
 * `hotkeyKeys(id)`.
 *
 * Binding the same id from two mounted surfaces runs both handlers, so a
 * shortcut stays owned by one surface — the palette that opens on it, not the
 * shell around it.
 */
export function useHotkey(id: HotkeyId, handler: HotkeyHandler, enabled = true): void {
  const handlerRef = useRef(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    if (!enabled) return;
    return subscribe(id, () => handlerRef.current());
  }, [id, enabled]);
}
